import { useLayoutEffect, useRef } from "react";

interface UseScrollRestorationOptions {
  /**
   * Key to uniquely identify the scroll position in sessionStorage.
   */
  key: string;
  containerId: string;
  /**
   * Whether to save the scroll position when the component unmounts.
   * Default is true.
   */
  saveOnUnmount?: boolean;
}

/**
 * Saves the scroll position of the specified container.
 */
export function saveScrollPosition(key: string, containerId: string): void {
  const scrollPosition = document.querySelector(`#${containerId}`)?.scrollTop ?? 0;
  sessionStorage.setItem(key, scrollPosition.toString());
}

/**
 * Restores the scroll position of the specified container.
 */
export function restoreScrollPosition(key: string, containerId: string): void {
  const savedPosition = sessionStorage.getItem(key);
  if (savedPosition) {
    const scrollToContainer = document.querySelector(`#${containerId}`);
    if (scrollToContainer && typeof scrollToContainer.scrollTo === "function") {
      scrollToContainer.scrollTo(0, Number.parseInt(savedPosition, 10));
    }

    sessionStorage.removeItem(key);
  }
}

/**
 * Clears the scroll position for a given key.
 */
export function clearScrollPosition(key: string): void {
  sessionStorage.removeItem(key);
}

/**
 * A hook to preserve and restore the scroll position of a page.
 */
export function useScrollRestoration(options: UseScrollRestorationOptions): void {
  const { key, containerId, saveOnUnmount = true } = options;
  const lastScrollPositionRef = useRef(0);

  // Track scroll position via events so we have the correct value even when
  // an ancestor has display:none (which resets scrollTop to 0 in the DOM).
  useLayoutEffect(() => {
    const container = document.querySelector(`#${containerId}`);
    if (!container) {
      return;
    }

    lastScrollPositionRef.current = container.scrollTop;
    const handleScroll = () => {
      lastScrollPositionRef.current = container.scrollTop;
    };
    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [containerId]);

  useLayoutEffect(() => {
    const container = document.querySelector(`#${containerId}`);
    const savedPosition = sessionStorage.getItem(key);

    if (!savedPosition || !container) {
      return () => {
        if (saveOnUnmount) {
          sessionStorage.setItem(key, lastScrollPositionRef.current.toString());
        }
      };
    }

    const position = Number.parseInt(savedPosition, 10);

    const doRestore = () => {
      container.scrollTo(0, position);
      sessionStorage.removeItem(key);
      lastScrollPositionRef.current = position;
    };

    // If visible, restore immediately. Otherwise wait until the container
    // becomes visible (e.g. ancestor removes display:none).
    if (container.getBoundingClientRect().height > 0) {
      doRestore();
    } else {
      const observer = new ResizeObserver((entries) => {
        if (entries[0]?.contentRect.height > 0) {
          doRestore();
          observer.disconnect();
        }
      });
      observer.observe(container);

      return () => {
        observer.disconnect();
        if (saveOnUnmount) {
          sessionStorage.setItem(key, lastScrollPositionRef.current.toString());
        }
      };
    }

    return () => {
      if (saveOnUnmount) {
        sessionStorage.setItem(key, lastScrollPositionRef.current.toString());
      }
    };
  }, [key, containerId, saveOnUnmount]);
}
